import React, {useState, useEffect} from 'react';
import GoodCard from './../../Components/Cards/Good';
import styles from './Css/ScrollGoods.module.css';
import { useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import PoweredByWebappz from "../../Components/Cards/PoweredByWebappz";
import {Card, Typography} from "@mui/material";
import Stack from "@mui/material/Stack";
import {
    normalizeStorefrontTemplate,
    STOREFRONT_TEMPLATES
} from '../../utils/storefrontTemplate';

function filterAndSortGoods(goodsObject, goodsParams) {
    const { categoryID, filterSearchGoods, textSearchGoods } = goodsParams;

    goodsObject = Object.values(goodsObject).filter(good => good !== undefined);

    if (categoryID !== 0) {
        goodsObject = goodsObject.filter(good => good.category === categoryID || good.autocategory === categoryID);
    }

    if (textSearchGoods && textSearchGoods.length > 0) {
        goodsObject = goodsObject.filter(good => good.title.toLowerCase().includes(textSearchGoods.toLowerCase()));
    }

    switch (filterSearchGoods) {
        case '0':
            goodsObject.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            break;
        case '1':
            goodsObject.sort((a, b) => parseFloat(b.cost) - parseFloat(a.cost));
            break;
        case '2':
            goodsObject.sort((a, b) => parseFloat(a.cost) - parseFloat(b.cost));
            break;
        default:
            break;
    }

    return goodsObject;
}


const GoodItem = ({ good, handleChangeShopIDSearchGoods, setOpenGoodModal, SettingsSession, currentPage, priority }) => {
    return (
        <div className={`col-${SettingsSession.col} p-absolute storefront-product-grid-item`}>
            <GoodCard
                currentPage={currentPage}
                handleChangeShopIDSearchGoods={handleChangeShopIDSearchGoods}
                good={good}
                setOpenGoodModalLocal={setOpenGoodModal}
                priority={priority}
            />
        </div>
    );
};

const BoutiqueIntro = ({ SettingsSession, goodsParams }) => {
    const { t } = useTranslation();
    const isBoutique = normalizeStorefrontTemplate(SettingsSession.storefront_template) === STOREFRONT_TEMPLATES.BOUTIQUE;
    const isMainCatalog = goodsParams.categoryID === 0
        && goodsParams.catalogID === 0
        && goodsParams.textSearchGoods.length === 0;

    if (!isBoutique || !isMainCatalog) {
        return null;
    }

    const displayName = String(SettingsSession.display_name || SettingsSession.shop_name || 'STORE')
        .replace(/_?bot$/i, '')
        .replace(/[_-]+/g, ' ')
        .trim();

    return (
        <section className="storefront-boutique-intro" aria-label={displayName}>
            <div className="storefront-boutique-hero">
                <span>{t('storefront_boutique_online_store')}</span>
                <h1>{displayName || 'STORE'}</h1>
            </div>
            <div className="storefront-boutique-stats">
                <article>
                    <small>{t('totalProducts')}</small>
                    <strong>{Number(SettingsSession.countGoods || 0).toLocaleString('ru-RU')}</strong>
                    <span>{t('pcs')}</span>
                </article>
                <article>
                    <small>{t('addedToday')}</small>
                    <strong>{Number(SettingsSession.addedToday || 0).toLocaleString('ru-RU')}</strong>
                    <span>{t('storefront_boutique_fresh_items')}</span>
                </article>
            </div>
            <div className="storefront-boutique-section-heading">
                <h2>{t('storefront_boutique_new_arrivals')}</h2>
                <span>{t('storefront_boutique_catalog_hint')}</span>
            </div>
        </section>
    );
};


const ScrollGoods = ({ panel_type, isLoadingGoods, hasCheckedGoods, currentPage, handleCloseBackdrop, setOpenGoodModal, handleCloseLoadingGoods, goodsParams, scrollContainerRef, isVisibleHeader, loadingAppendGoods, handleChangeShopIDSearchGoods }) => {
    const SettingsSession = useSelector(state => state.shop.shop.settings);
    const goodsObject = useSelector(state => state.goods.goods);
    const [goods, setGoods] = useState([]);
    const [headerHeight, setHeaderHeight] = useState(120);
    const {t} = useTranslation();
    const [error, setError] = useState(false);
    const [renderedGoodsCount, setRenderedGoodsCount] = useState(6);
    const [canRevealMoreGoods, setCanRevealMoreGoods] = useState(false);
    const revealMoreRef = React.useRef(null);

    const updateHeaderHeight = React.useCallback(() => {
        const header = document.querySelector('header');
        if (header) {
            setHeaderHeight(header.clientHeight - (window.Telegram.WebApp.safeAreaInset !== undefined ? window.Telegram.WebApp.safeAreaInset.top * 2 : 0));
        }
    }, []);

    useEffect(() => {
        if(goodsObject[0] !== undefined && goodsObject[0].error !== undefined) {
            setError(goodsObject[0].error);
        }
    }, [goodsObject])

    useEffect(() => {
        const header = document.querySelector('header');
        if (header) {
            const resizeObserver = new ResizeObserver(updateHeaderHeight);
            resizeObserver.observe(header);
            updateHeaderHeight();
            return () => resizeObserver.disconnect();
        }
    }, [updateHeaderHeight]);

    useEffect(() => {
        const handleSafeAreaChanged = () => updateHeaderHeight();
        window.Telegram.WebApp.onEvent('safeAreaChanged', handleSafeAreaChanged);

        return () => {
            if (window.Telegram.WebApp.offEvent !== undefined) {
                window.Telegram.WebApp.offEvent('safeAreaChanged', handleSafeAreaChanged);
            }
        }
    }, [updateHeaderHeight]);

    useEffect(() => {
        if (SettingsSession.show !== undefined) {
            setGoods(Object.values(filterAndSortGoods(goodsObject, goodsParams)));
        } else {
            setGoods(Object.values(goodsObject));
        }
    }, [goodsParams, goodsObject, SettingsSession.show]);

    useEffect(() => {
        setRenderedGoodsCount(6);
        setCanRevealMoreGoods(false);
    }, [
        goodsParams.categoryID,
        goodsParams.filterSearchGoods,
        goodsParams.textSearchGoods,
        goodsParams.catalogID,
        goodsParams.shopIDSearchGoods
    ]);

    useEffect(() => {
        const enableReveal = () => {
            if (window.scrollY > 24) {
                setCanRevealMoreGoods(true);
            }
        };

        window.addEventListener('scroll', enableReveal, { passive: true });
        return () => window.removeEventListener('scroll', enableReveal);
    }, []);

    useEffect(() => {
        const sentinel = revealMoreRef.current;
        if (!canRevealMoreGoods || !sentinel || renderedGoodsCount >= goods.length) {
            return undefined;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries.some(entry => entry.isIntersecting)) {
                setRenderedGoodsCount((current) => Math.min(current + 6, goods.length));
            }
        }, { rootMargin: '0px 0px 80px 0px' });

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [canRevealMoreGoods, goods.length, renderedGoodsCount]);

    if(error !== false) {
        return (
            <>
                <div style={{height: headerHeight}}></div>
                <Stack>
                    <Card>
                        {
                            error === 'block_50_products' ? (
                                <>
                                    {
                                        SettingsSession.t === true ? (
                                            <Typography>
                                                {t('block_catalog_admin')}
                                            </Typography>
                                        ) : (
                                            <Typography variant={"small"}>{t('block_catalog_user')}</Typography>
                                        )
                                    }
                                </>
                            ) : null
                        }
                    </Card>
                </Stack>
            </>
        )
    }

    return (
        <>
        <div style={{height: headerHeight}}></div>
            <div
                ref={scrollContainerRef}
                className={`${panel_type ? styles.contentPageByPanel : styles.contentPage} row w-100 mainBgColor storefront-goods-grid`}
                style={{
                    transition: "0.5s all"
                }}
            >
                <>
                        <BoutiqueIntro SettingsSession={SettingsSession} goodsParams={goodsParams} />
                        {goods.slice(0, renderedGoodsCount).map((good, index) => (
                            <GoodItem
                                currentPage={currentPage}
                                key={good.id || `${good.shopID}-${index}`}
                                good={good}
                                handleChangeShopIDSearchGoods={handleChangeShopIDSearchGoods}
                                setOpenGoodModal={setOpenGoodModal}
                                SettingsSession={SettingsSession}
                                priority={index < 6}
                            />
                        ))}
                        {renderedGoodsCount < goods.length && (
                            <div ref={revealMoreRef} aria-hidden="true" style={{ width: '100%', height: 1 }} />
                        )}
                        {
                            !isLoadingGoods && hasCheckedGoods && goods.length < 1 && goodsParams.textSearchGoods.length > 0 ? (
                                    <div className="w-100">
                                        <div className="w-100 text-center mt-2">
                                            <p>
                                                {t('no_products_with_the_name_were_found', {name: goodsParams.textSearchGoods})}
                                            </p>
                                        </div>
                                    </div>
                            ) : null
                        }

                        {
                            !isLoadingGoods && hasCheckedGoods && goods.length < 1 && goodsParams.textSearchGoods.length < 1 ? (
                                    <div className="w-100">
                                        <div className="w-100 text-center mt-2">
                                            <p>{t('products_were_found_in_the_store')}</p>
                                        </div>
                                    </div>
                            ) : null
                        }
                </>
                {
                    loadingAppendGoods && (
                        <div className="d-flex justify-content-center w-100">
                            <CircularProgress />
                        </div>
                    )
                }
                {hasCheckedGoods && <PoweredByWebappz />}
                {
                    panel_type ? (
                        <div className={styles.bottomPanelSpacer}></div>
                    ) : null
                }
            </div>
        </>
    );
};

export default ScrollGoods;
